<template>
    <div class="multiple-report-picker">
        <loading 
            :active="reportDownloading"
            :can-cancel="false" 
            :is-full-page="true"
        />
        <Multiselect
            v-model="selected"
            :options="processedOptions"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :placeholder="'Izberite poročila, ki jih želite prenesti'"
            :select-label="$t('general:select')"
            :selected-label="$t('general:selected')"
            :deselect-label="$t('general:remove')"

            v-bind="additionalAttrs"
        >
            <template slot="noResult">
                {{ $t('general:search:no_results')}}
            </template>
        </Multiselect>
        <div>
            <button
                :class="[{'disabled': selected.length === 0}, 'btn btn-info btn-sm btn-download']"
                @click="getReports"
            >
                Prenesi izbrana poročila <i class="fa fa-download"></i>
            </button>
        </div>
    </div>
</template>

<script>
import ApiService from '@/components/utils/api.service';
import Multiselect from 'vue-multiselect'
import Loading from 'vue-loading-overlay'
import { Swal } from "@/components/utils/swal.alerts.js";

export default {
    name: "MultipleReportPicker",
    components: {
        Multiselect,
        Loading,
    },
    props: {
        analysis: {
            type: Number,
            required: true,
        }
    },
    data() {
        return {
            selected: [],
            options: [],
            reportDownloading: false,
        }
    },
    methods: {
        getReports() {
            if (this.selected.length === 0) {
                return;
            }

            this.reportDownloading = true;
            if (this.selected.length === 1) {
                const reportId = this.selected[0].value;
                ApiService
                    .get(`/amas/analysis/${this.analysis}/report/download/${reportId}`, {
                        responseType: "blob"
                    })
                    .then(res => this.downloadReport(res.data, `Porocilo-${this.analysis}`))
                    .catch(error => {
                        Swal({
                            title: "",
                            text: "Napaka pri izvozu poročila",
                            icon: "error",
                        });
                        throw error;
                    })
                    .finally(() => {
                        this.reportDownloading = false;
                    })
            } else {
                const reportIds = this.selected.map(option => option.value);
                ApiService
                    .post(`/amas/analysis/${this.analysis}/report/downloadmultiple`, {
                        reports: reportIds
                    }, {
                        responseType: "blob"
                    })
                    .then(res => this.downloadReport(res.data, `Analiza-${this.analysis}.zip`))
                    .catch(error => {
                        Swal({
                            title: "",
                            text: "Napaka pri izvozu poročil",
                            icon: "error",
                        });
                        throw error;
                    })
                    .finally(() => {
                        this.reportDownloading = false;
                    })
            }
        },
        downloadReport(data, filename) {
            const href = URL.createObjectURL(data);

            const link = document.createElement("a");
            link.href = href;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        },
    },
    computed: {
        hasMultipleGroups() {
            const groups = new Set(this.options.map(option => option.report_template_group_name));
            return groups.size > 1;
        },
        processedOptions() {
            if (this.hasMultipleGroups) {
                const sortedReports = [...this.options].sort((a, b) => 
                    a.report_name.localeCompare(b.report_name)
                );

                const groupedReports = {};
                sortedReports.forEach(report => {
                    const groupName = report.report_template_group_name || this.$t("general:other_reports");
                    if (!groupedReports[groupName]) {
                        groupedReports[groupName] = [];
                    }
                    groupedReports[groupName].push({
                        text: report.report_name,
                        value: report.id
                    });
                });

                // sort by name, "Other" group is always last
                const otherGroupKey = this.$t("general:other_reports");
                return Object.keys(groupedReports)
                    .sort((a, b) => (a === otherGroupKey) - (b === otherGroupKey) || a.localeCompare(b))
                    .map(groupName => ({
                        label: groupName,
                        options: groupedReports[groupName]
                    }));
            } else {
                return this.options.map(option => ({
                    text: option.report_name,
                    value: option.id
                }));
            }
        },
        additionalAttrs() {
            // handle multiple groups or flat list
            if (this.hasMultipleGroups) {
                return {
                    groupSelect: false,
                    groupValues: "options",
                    groupLabel: "label",
                    groupAttributes: ["label"],
                    label: "text",
                    trackBy: "value",
                }
            }
            return {
                label: "text",
                trackBy: "value",
            }
        }
    },
    mounted() {
        ApiService
            .get("/api/v1/current-user")
            .then(response => {
                const reports = response.data.available_report_templates;
                if (reports.length > 0) {
                    this.options = reports
                }
            });
    },
}
</script>

<style>
.multiple-report-picker {
    display: flex;
    flex-direction: row;
    gap: 10px;
}
@media (max-width: 768px) {
    .multiple-report-picker {
        flex-direction: column-reverse;
        align-items: flex-end;
    }
}
.multiple-report-picker .btn-download {
    margin-top: 5px;
}
.multiple-report-picker:has(.multiselect__option--group) .multiselect__element:not(:has(.multiselect__option--group)) span {
    padding-left: 10px;
}
</style>
